import type { AnyRoute } from "@tanstack/react-router"

import type { GraphQLSelection } from "../remote/graphql-document"
import type { Session } from "../session/session"
import type { Application } from "./application"
import type { ApplicationPluginInitializationContext } from "./plugin-runtime"
import type { LocalizationResources } from "../localization/localization-resources"
import type { LocalizedMessage } from "../localization/localized-message"

type MaybePromise<T> = T | Promise<T>

export interface ApplicationPluginRouteFactoryContext {
  readonly parentRoute: AnyRoute
}

export type ApplicationPluginRouteFactory = (context: ApplicationPluginRouteFactoryContext) => readonly AnyRoute[]

export interface ApplicationPluginAuthorizationContext {
  readonly application: Application
  readonly session: Session
}

export interface ApplicationPluginAvailability {
  readonly isAvailable: boolean
  readonly featureId?: string | null
}

export type ApplicationBrandText = string | LocalizedMessage

export interface ApplicationBranding {
  readonly name: ApplicationBrandText
  readonly subtitle?: ApplicationBrandText
}

export interface ApplicationPlugin {
  readonly id: string
  readonly parentId?: string
  readonly routes?: ApplicationPluginRouteFactory
  readonly localization?: readonly LocalizationResources[]
  readonly onAuthenticatedRootQuery?: () => GraphQLSelection<unknown>
  readonly onAuthenticatedRootQueryResult?: (useCases: unknown) => MaybePromise<ApplicationPluginAvailability | void>
  readonly isAuthorized?: (context: ApplicationPluginAuthorizationContext) => boolean
  readonly initialize?: (context: ApplicationPluginInitializationContext) => MaybePromise<void>
}

export interface ApplicationPluginOptions<TAuthenticatedUseCases = unknown> {
  readonly id: string
  readonly parent?: ApplicationPlugin
  readonly routes?: ApplicationPluginRouteFactory
  readonly localization?: readonly LocalizationResources[]
  readonly onAuthenticatedRootQuery?: () => GraphQLSelection<TAuthenticatedUseCases>
  readonly onAuthenticatedRootQueryResult?: (
    useCases: TAuthenticatedUseCases
  ) => MaybePromise<ApplicationPluginAvailability | void>
  readonly isAuthorized?: (context: ApplicationPluginAuthorizationContext) => boolean
  readonly initialize?: (context: ApplicationPluginInitializationContext) => MaybePromise<void>
}

export interface ApplicationDefinition {
  readonly branding: ApplicationBranding
  readonly plugins: readonly ApplicationPlugin[]
}

export function defineApplicationPlugin<TAuthenticatedUseCases = unknown>(
  options: ApplicationPluginOptions<TAuthenticatedUseCases>
): ApplicationPlugin {
  const id = normalizePluginId(options.id)
  const parentId = options.parent == null ? undefined : normalizePluginId(options.parent.id)

  if (parentId === id) {
    throw new Error(`Application plugin ${id} cannot be its own parent.`)
  }

  return Object.freeze({
    id,
    parentId,
    routes: options.routes,
    localization: options.localization == null ? undefined : Object.freeze([...options.localization]),
    onAuthenticatedRootQuery: options.onAuthenticatedRootQuery,
    onAuthenticatedRootQueryResult:
      options.onAuthenticatedRootQueryResult == null
        ? undefined
        : (useCases: unknown) => options.onAuthenticatedRootQueryResult!(useCases as TAuthenticatedUseCases),
    isAuthorized: options.isAuthorized,
    initialize: options.initialize,
  })
}

export function defineApplication(options: {
  readonly branding?: ApplicationBranding
  readonly plugins: readonly ApplicationPlugin[]
}): ApplicationDefinition {
  const plugins = options.plugins.map((plugin) =>
    Object.freeze({
      id: plugin.id,
      parentId: plugin.parentId,
      routes: plugin.routes,
      localization: plugin.localization == null ? undefined : Object.freeze([...plugin.localization]),
      onAuthenticatedRootQuery: plugin.onAuthenticatedRootQuery,
      onAuthenticatedRootQueryResult: plugin.onAuthenticatedRootQueryResult,
      isAuthorized: plugin.isAuthorized,
      initialize: plugin.initialize,
    })
  )
  const byId = new Map<string, ApplicationPlugin>()

  for (const plugin of plugins) {
    const id = normalizePluginId(plugin.id)
    if (id !== plugin.id) {
      throw new Error(`Application plugin id must be normalized: ${plugin.id}.`)
    }
    if (byId.has(id)) {
      throw new Error(`Duplicate application plugin id: ${id}.`)
    }
    byId.set(id, plugin)
  }

  for (const plugin of plugins) {
    if (plugin.parentId != null && !byId.has(plugin.parentId)) {
      throw new Error(`Application plugin ${plugin.id} references unregistered parent ${plugin.parentId}.`)
    }
  }

  for (const plugin of plugins) {
    assertNoParentCycle(plugin, byId)
  }

  return Object.freeze({
    branding: Object.freeze({
      name: options.branding?.name ?? "Florisoft",
      subtitle: options.branding?.subtitle,
    }),
    plugins: Object.freeze(plugins),
  })
}

function normalizePluginId(id: string) {
  const normalized = id.trim()
  if (normalized.length === 0) {
    throw new Error("Application plugin id must not be empty.")
  }
  return normalized
}

function assertNoParentCycle(plugin: ApplicationPlugin, byId: ReadonlyMap<string, ApplicationPlugin>) {
  const visited = new Set<string>()
  let current: ApplicationPlugin | undefined = plugin

  while (current != null) {
    if (visited.has(current.id)) {
      throw new Error(`Application plugin parent cycle detected at ${current.id}.`)
    }
    visited.add(current.id)
    current = current.parentId == null ? undefined : byId.get(current.parentId)
  }
}
